/**
 * ProgressionTemplate — Cinematic Staged Reveal
 * ---------------------------------------------------------------
 * Renders an Observed Progression sequence:
 *
 *   MINIMIZATION
 *      ↓
 *   DEFLECTION
 *      ↓
 *   EMOTIONAL REVERSAL
 *
 * Each stage fades in with a long restrained hold. Surrounding
 * stages dim slightly when a new one lands. No bounce, no neon.
 * Forensic archive aesthetic, ends on "Observed Progression
 * Archived." Pulls stages from the sim's spiralMoment / dialogue
 * archetype if no explicit progression is supplied.
 */

import { AbsoluteFill, Sequence, useCurrentFrame, interpolate } from "remotion";
import type { SimulationContent } from "@/lib/contentGenerator";
import { FPS, PALETTE } from "../utils/timing";
import { BackgroundField } from "../components/BackgroundField";
import { CTAFrame } from "../components/CTAFrame";

/** Stage labels are inferred per archetype if not provided. */
const ARCHETYPE_PROGRESSION: Record<string, string[]> = {
  invalidation: ["Charm Framing", "Minimization", "Emotional Reversal"],
  blame_shift: ["Soft Open", "Blame Shift", "Guilt Induction"],
  deflection: ["Topic Drift", "Deflection", "Counter-Attack"],
  guilt_pressure: ["Appeal", "Guilt Pressure", "Withdrawal Threat"],
};

interface Props {
  sim: SimulationContent;
  /** Optional explicit progression stages — overrides archetype inference. */
  stages?: string[];
}

const STAGE_HOLD = Math.round(FPS * 1.6); // 1.6s per stage
const STAGE_FADE = Math.round(FPS * 0.7);
const ARROW_GAP = Math.round(FPS * 0.5);

export const ProgressionTemplate: React.FC<Props> = ({ sim, stages }) => {
  const inferred =
    stages && stages.length
      ? stages
      : ARCHETYPE_PROGRESSION[sim.archetype] ??
        ["Charm Framing", "Pressure", "Reversal"];

  const frame = useCurrentFrame();

  // Each stage gets STAGE_HOLD + ARROW_GAP frames
  const perStage = STAGE_HOLD + ARROW_GAP;
  const dialogueFrames = perStage * inferred.length + STAGE_HOLD;
  const ctaFrames = Math.round(FPS * 3);
  const ctaStart = dialogueFrames;

  return (
    <AbsoluteFill style={{ backgroundColor: PALETTE.bg }}>
      <BackgroundField />

      <Sequence durationInFrames={dialogueFrames}>
        <AbsoluteFill
          style={{
            padding: "220px 80px 200px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {/* Section header — restrained */}
          <div
            style={{
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: 18,
              letterSpacing: "0.5em",
              textTransform: "uppercase",
              color: PALETTE.textDim,
              opacity: 0.75,
              marginBottom: 12,
            }}
          >
            Observed Progression
          </div>
          <div
            style={{
              fontFamily:
                'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
              fontSize: 22,
              fontStyle: "italic",
              color: PALETTE.textDim,
              opacity: 0.55,
              marginBottom: 80,
            }}
          >
            Patterns observed during conversational escalation.
          </div>

          {/* Stage column */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 28,
            }}
          >
            {inferred.map((stage, i) => {
              const stageStart = i * perStage;
              const stageRevealed = stageStart + STAGE_FADE;
              // Active = the most recently revealed stage
              const newestIdx = Math.min(
                inferred.length - 1,
                Math.max(0, Math.floor(frame / perStage))
              );
              const isActive = i === newestIdx;
              const fade = interpolate(
                frame,
                [stageStart, stageRevealed],
                [0, 1],
                { extrapolateRight: "clamp", extrapolateLeft: "clamp" }
              );
              const lift = interpolate(
                frame,
                [stageStart, stageRevealed],
                [10, 0],
                { extrapolateRight: "clamp", extrapolateLeft: "clamp" }
              );
              const dim = isActive ? 1 : 0.32;
              const glow = isActive
                ? `0 0 50px rgba(201,169,106,0.10)`
                : "none";

              return (
                <div
                  key={`${stage}-${i}`}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    opacity: fade * dim,
                    transform: `translateY(${lift}px)`,
                  }}
                >
                  <div
                    style={{
                      fontFamily:
                        'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                      fontSize: 56,
                      fontWeight: 400,
                      letterSpacing: "0.32em",
                      textTransform: "uppercase",
                      color: PALETTE.silver,
                      textAlign: "center",
                      textShadow: glow,
                    }}
                  >
                    {stage}
                  </div>
                  {i < inferred.length - 1 && (
                    <div
                      style={{
                        marginTop: 24,
                        height: 56,
                        width: 1,
                        background:
                          "linear-gradient(to bottom, rgba(103,232,249,0.35), rgba(103,232,249,0.04))",
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </AbsoluteFill>
      </Sequence>

      <Sequence from={ctaStart} durationInFrames={ctaFrames}>
        <CTAFrame preset="progression" />
      </Sequence>
    </AbsoluteFill>
  );
};

/** Total runtime helper for callers planning <Composition> frames. */
export function progressionDurationFrames(stageCount: number): number {
  const perStage = STAGE_HOLD + ARROW_GAP;
  return perStage * stageCount + STAGE_HOLD + Math.round(FPS * 3);
}
